import { useRef } from 'react';

/**
 * Unit Registry: Persistent snapshot of all units by instanceId
 * This allows us to reference units even after they're removed from the battlefield
 * 
 * Usage:
 * const unitRegistryRef = useUnitRegistry();
 * updateUnitRegistry(gameState, unitRegistryRef);
 * const unit = getUnitFromRegistry(instanceId, unitRegistryRef);
 */

/**
 * Hook to create and manage a unit registry
 * @returns {React.MutableRefObject<Map>} - Reference to the unit registry Map
 */
export function useUnitRegistry() {
  return useRef(new Map()); // Map<instanceId, {unit data, playerId, zone, index, location}>
}

/**
 * Helper to build/update unit registry from game state
 * Merges new unit data into registry, preserving old entries if units are removed
 * @param {Object} gameState - Current game state
 * @param {React.MutableRefObject<Map>} registryRef - Reference to the unit registry
 */
export function updateUnitRegistry(gameState, registryRef) {
  if (!gameState?.state || !registryRef) return;
  
  const allPlayerIds = gameState.players || Object.keys(gameState.state);
  const registry = registryRef.current;
  
  allPlayerIds.forEach(playerId => {
    const player = gameState.state[playerId];
    if (!player) return;
    
    // Register units on battlefield
    ['front', 'flank'].forEach(zone => {
      if (player.playedOrders?.[zone]) {
        player.playedOrders[zone].forEach((unit, idx) => {
          if (unit && unit.instanceId) {
            // Merge with existing registry entry if it exists, otherwise create new
            const existing = registry.get(unit.instanceId);
            registry.set(unit.instanceId, {
              ...(existing || {}),
              ...unit,
              playerId: playerId,
              zone: zone,
              index: idx,
              location: 'battlefield',
              timestamp: Date.now()
            });
          }
        });
      }
    });
    
    // Register units in hand
    if (player.hand) {
      player.hand.forEach((unit) => {
        if (unit && unit.instanceId) {
          const existing = registry.get(unit.instanceId);
          registry.set(unit.instanceId, {
            ...(existing || {}),
            ...unit,
            playerId: playerId,
            location: 'hand',
            timestamp: Date.now()
          });
        }
      });
    }
    
    // Register units in completedOrders (dead units)
    if (player.completedOrders) {
      player.completedOrders.forEach((unit) => {
        if (unit && unit.instanceId) {
          const existing = registry.get(unit.instanceId);
          registry.set(unit.instanceId, {
            ...(existing || {}),
            ...unit,
            playerId: playerId,
            location: 'completedOrders',
            timestamp: Date.now()
          });
        }
      });
    }
  });
}

/**
 * Get unit from registry by instanceId
 * @param {string} instanceId - Unit instance ID
 * @param {React.MutableRefObject<Map>} registryRef - Reference to the unit registry
 * @returns {Object|null} - Unit data or null if not found
 */
export function getUnitFromRegistry(instanceId, registryRef) {
  if (!registryRef || !instanceId) return null;
  return registryRef.current.get(instanceId) || null;
}

